import {
  getAllFiles,
  getFiles,
  googleUserAtom,
} from 'infrastructure/googleDrive'
import { loadable } from 'jotai/utils'
import { atom } from 'infrastructure/state/jotai.wrapper'
import { SwapData, SwapUpdates } from './types'
import { INode } from 'shared/types/tree'
import { getGradeId, nodesOfTypeWithContent } from '../../utility/TreeHelpers'
import {
  idsForNode,
  UpdateMethod,
  UpdateType,
} from '../../tree/nodeUpdates/utility'
import { NodeType } from 'shared/types/ingestion'
import { getFileId } from '../../tree/masters/presentations/utility'
import { createIdMap } from '../../version_mapping/utility'
import { UpdateLeafData } from '../../tree/nodeUpdates/types'

export const presentationSwapTreeAtom = atom<INode | undefined>(undefined)
export const presentationSwapTreeIdMapAtom = atom((get) => {
  const tree = get(presentationSwapTreeAtom)
  if (!tree) return
  return createIdMap(tree)
})

export const presentationsByGradeAtom = loadable(
  atom(async (get) => {
    const { credential } = get(googleUserAtom)
    if (!credential?.accessToken) return
    const data = await getFiles(
      credential.accessToken,
      '1Q871UT9WZUK1_NQ4k2apIVxS4yIrpG8J',
    )
    const { files } = JSON.parse(data) as { files: any[] }
    return files.reduce<Record<string, string>>(
      (acc, file: { name: string; id: string }) => {
        if (file.name.includes('-')) {
          acc[file.name.split('-').pop()!.trim()] = file.id
        }
        return acc
      },
      {},
    )
  }),
)

export const presentationsSheetIdForGradeAtom = atom((get) => {
  const tree = get(presentationSwapTreeAtom)
  if (!tree) return
  const gradeId = getGradeId(tree)
  const presentations = get(presentationsByGradeAtom)
  if (presentations.state !== 'hasData' || !presentations.data || !gradeId)
    return
  switch (gradeId) {
    case 1:
      return presentations.data.K
    case 10:
      return presentations.data['A 1']
    case 11:
      return presentations.data.GEO
    case 12:
      return presentations.data['A 2']
    default:
      return presentations.data[String(gradeId - 1)]
  }
})

export const presentationsForGradeAtom = loadable(
  atom(async (get) => {
    const sheetId = get(presentationsSheetIdForGradeAtom)
    if (!sheetId) return
    const { credential } = get(googleUserAtom)
    if (!credential?.accessToken) return
    const files = await getAllFiles(credential.accessToken, sheetId)
    if (!Array.isArray(files)) return
    return files.reduce<SwapData[]>((acc, file) => {
      if (!file.mimeType.includes('/vnd.google-apps.presentation')) {
        return acc
      }
      return [
        ...acc,
        {
          name: file.name.trim(),
          url: `https://docs.google.com/presentation/d/${file.id}/preview`,
          id: file.id,
        },
      ]
    }, [])
  }),
)

function filterUpdatesForTree(swapData: SwapData[], tree: INode) {
  const displayGrade = getGradeId(tree)! - 1
  const startsWith =
    displayGrade > 8 ? '9–12.' : displayGrade === 0 ? 'K' : `${displayGrade}.`
  const filtered = swapData.filter(({ name }) => {
    return name.startsWith(startsWith)
  })
  return filtered
}

function deletedLeavesFromNode(tree: INode): UpdateLeafData[] {
  const nodes = nodesOfTypeWithContent({
    tree,
    types: [NodeType.AttachmentsList],
    content: ['Additional Resources'],
  })
  return nodes.reduce<UpdateLeafData[]>((acc, node) => {
    const leaf = node.leaves?.find(
      (leaf) => leaf.body.file.name === 'PowerPoint Slides',
    )
    if (!leaf) return acc
    acc.push({
      type: UpdateType.leaf,
      ids: idsForNode(leaf),
      method: UpdateMethod.delete,
    })
    return acc
  }, [])
}

function getNodeWithFileName(fileName: string, tree: INode) {
  let searchPart = fileName
    .split('.gs')[0]
    .replaceAll('.0', '.')
    .replaceAll('/', ':')

  const [node] = nodesOfTypeWithContent({
    tree,
    types: [NodeType.AttachmentsList],
    content: [searchPart],
    caseSensitive: false,
  })
  if (node) return node
  const [_, ...rest] = searchPart.split(' ')
  searchPart = rest.join(' ').split(')')[0].trim()
  const nodes = nodesOfTypeWithContent({
    tree,
    types: [NodeType.AttachmentsList],
    content: [searchPart],
  })
  return nodes.pop()
}

function presentationUrlForId(presentationId: string) {
  return `https://docs.google.com/presentation/d/${presentationId}/preview`
}

function getAllSwaps(filtered: SwapData[], tree: INode) {
  return filtered.reduce<SwapUpdates>(
    ({ completed, updates, notFound, wrongIdCount }, { name, id }) => {
      const node = getNodeWithFileName(name, tree)
      if (!node) {
        notFound.push(name)
      } else {
        const url = presentationUrlForId(id)
        const leaf = node.leaves?.[0]
        if (leaf?.body.file.url === url) {
          completed.push(node.id)
        } else if (leaf) {
          if (!leaf.body.file.url.includes(id)) {
            wrongIdCount += 1
          }
          const file_id = getFileId(url)
          const newBody = {
            ...leaf.body,
            file_id,
            file: {
              ...leaf.body.file,
              url,
            },
          }
          const leafData: UpdateLeafData = {
            type: UpdateType.leaf,
            method: UpdateMethod.update,
            ids: idsForNode(leaf),
            params: {
              body: newBody,
            },
          }
          updates.push(leafData)
        }
      }

      return {
        completed,
        updates,
        notFound,
        wrongIdCount,
      }
    },
    { completed: [], updates: [], notFound: [], wrongIdCount: 0 },
  )
}

export const allSwapUpdatesAtom = atom((get) => {
  const tree = get(presentationSwapTreeAtom)
  if (!tree) return
  const presentationIds = get(presentationsForGradeAtom)
  if (presentationIds.state !== 'hasData' || !presentationIds.data) {
    return
  }
  const filtered = filterUpdatesForTree(presentationIds.data, tree)
  const updates = getAllSwaps(filtered, tree)
  const deletes = deletedLeavesFromNode(tree)
  return {
    ...updates,
    updates: [...updates.updates, ...deletes],
  }
})
